/**
 * dawn-cart-ajax.js
 * Improved AJAX cart interactions for Dawn theme
 */

const DawnCartAjax = {
  /**
   * Initialize the AJAX cart functionality
   */
  init: function() {
    // Set up initial event delegation for upsell add buttons and protection toggle
    this.setupEventListeners();
    
    // Log initialization
    console.log('Dawn AJAX Cart initialized');
  },
  
  /**
   * Add item to cart via AJAX
   * @param {Number} variantId - The variant ID to add
   * @param {Number} quantity - Quantity to add
   * @param {Function} callback - Optional callback function
   */
  addItem: function(variantId, quantity, callback) {
    if (!variantId) return;
    
    // Show loading state
    this.showCartLoading();
    
    const formData = {
      items: [{
        id: variantId,
        quantity: quantity || 1
      }]
    };
    
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })
    .then(response => response.json())
    .then(data => {
      // Force a complete redraw of the cart drawer
      this.updateCartDrawer(true, () => {
        if (callback && typeof callback === 'function') {
          callback(data);
        }
      });
    })
    .catch(error => {
      console.error('Error adding item to cart:', error);
      this.hideCartLoading();
    });
  },
  
  /**
   * Change item quantity in cart via AJAX
   * @param {String} key - Cart item key
   * @param {Number} quantity - New quantity (0 to remove)
   * @param {Function} callback - Optional callback function
   */
  changeItem: function(key, quantity, callback) {
    if (!key) return;
    
    // Show loading state
    this.showCartLoading();
    
    const formData = {
      id: key,
      quantity: quantity
    };
    
    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })
    .then(response => response.json())
    .then(data => {
      // Force a complete redraw of the cart drawer
      this.updateCartDrawer(true, () => {
        if (callback && typeof callback === 'function') {
          callback(data);
        }
      });
    })
    .catch(error => {
      console.error('Error changing item in cart:', error);
      this.hideCartLoading();
    });
  },
  
  /**
   * Get the cart data and update all related elements
   * @param {Boolean} forceDrawerUpdate - Whether to force a complete drawer update
   * @param {Function} callback - Optional callback function
   */
updateCartDrawer: function(forceDrawerUpdate = false, callback) {
  fetch('/cart.js')
    .then(response => response.json())
    .then(cartData => {
      // IMPROVED: Store the user's intent for shipping protection state
      const protectionToggle = document.querySelector('.cart-shipping-protection__checkbox');
      const protectionIntent = protectionToggle ? protectionToggle.checked : false;
      
      // Update cart count bubbles
      this.updateCartCountBubbles(cartData);
      
      // Dispatch the cart:refresh event for other components to use
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          cart: cartData,
          protectionIntent: protectionIntent // Pass along user intent
        }
      }));
      
      // Force update if requested or if cart drawer is open
      if (forceDrawerUpdate || this.isCartDrawerOpen()) {
        this.fetchAndUpdateCartDrawerHTML(() => {
          // IMPROVED: Ensure protection state matches user intent after drawer update
          const updatedToggle = document.querySelector('.cart-shipping-protection__checkbox');
          if (updatedToggle && updatedToggle.checked !== protectionIntent) {
            updatedToggle.checked = protectionIntent;
            
            // If toggle state doesn't match cart contents, trigger an update
            if (protectionIntent && !updatedToggle.getAttribute('data-protection-cart-key')) {
              const variantId = updatedToggle.getAttribute('data-protection-variant-id');
              if (variantId) {
                this.addItem(variantId, 1);
              }
            } else if (!protectionIntent && updatedToggle.getAttribute('data-protection-cart-key')) {
              const itemKey = updatedToggle.getAttribute('data-protection-cart-key');
              if (itemKey) {
                this.changeItem(itemKey, 0);
              }
            }
          }
          
          this.hideCartLoading();
          if (callback && typeof callback === 'function') {
            callback();
          }
        });
      } else {
        this.hideCartLoading();
        if (callback && typeof callback === 'function') {
          callback();
        }
      }
    })
    .catch(error => {
      console.error('Error getting cart data:', error);
      this.hideCartLoading();
    });
},
  
  /**
   * Save the current state of shipping protection toggle
   * @return {Object} - The saved state
   */
  saveShippingProtectionState: function() {
    const toggle = document.querySelector('.cart-shipping-protection__checkbox');
    if (!toggle) return null;
    
    return {
      checked: toggle.checked,
      variantId: toggle.getAttribute('data-protection-variant-id'),
      cartKey: toggle.getAttribute('data-protection-cart-key')
    };
  },
  
  /**
   * Restore shipping protection state after cart drawer update
   * @param {Object} state - The saved state from saveShippingProtectionState
   */
  restoreShippingProtectionState: function(state) {
    if (!state) return;
    
    const toggle = document.querySelector('.cart-shipping-protection__checkbox');
    if (!toggle) return;
    
    // Restore variant ID and cart key
    toggle.setAttribute('data-protection-variant-id', state.variantId || '');
    toggle.setAttribute('data-protection-cart-key', state.cartKey || '');
    
    // We don't need to set checked state manually as it should be correct
    // based on the cart state, but we can if there's a discrepancy
    if (toggle.checked !== state.checked) {
      toggle.checked = state.checked;
    }
  },
  
  /**
   * Fetch fresh cart drawer HTML and update the DOM
   * @param {Function} callback - Optional callback function
   */
  fetchAndUpdateCartDrawerHTML: function(callback) {
    // Get a fresh render of the cart drawer section
    fetch(window.location.pathname + '?section_id=cart-drawer')
      .then(response => response.text())
      .then(responseText => {
        // Create a temporary element to hold the response
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = responseText;
        
        // Find the cart drawer content in the response
        // Dawn's format is typically a <cart-drawer> element inside a <div> wrapper
        const newCartDrawerContent = tempDiv.querySelector('cart-drawer');
        
        if (!newCartDrawerContent) {
          console.error('Could not find cart-drawer in response');
          this.fallbackCartUpdate();
          return;
        }
        
        // Get the current cart drawer
        const currentCartDrawer = document.querySelector('cart-drawer');
        if (!currentCartDrawer) {
          console.error('Could not find cart-drawer in document');
          return;
        }
        
        // Update the cart drawer content
        // We use replaceChildren to maintain event handlers on the parent element
        const itemsContainer = currentCartDrawer.querySelector('cart-drawer-items');
        if (itemsContainer) {
          const newItemsContainer = newCartDrawerContent.querySelector('cart-drawer-items');
          if (newItemsContainer) {
            // Replace just the items container content
            while (itemsContainer.firstChild) {
              itemsContainer.removeChild(itemsContainer.firstChild);
            }
            
            while (newItemsContainer.firstChild) {
              itemsContainer.appendChild(newItemsContainer.firstChild);
            }
          }
        }
        
        // Check for shipping protection in the new content
        this.syncShippingProtectionElement(newCartDrawerContent, currentCartDrawer);
        
        // Update the cart footer (totals, etc.)
        const footerContainer = currentCartDrawer.querySelector('.drawer__footer');
        if (footerContainer) {
          const newFooterContainer = newCartDrawerContent.querySelector('.drawer__footer');
          if (newFooterContainer) {
            footerContainer.innerHTML = newFooterContainer.innerHTML;
          }
        }
        
        // Make sure the cart drawer is open
        this.openCartDrawer();
        
        // Re-initialize components and update upsells
        this.reinitializeCartComponents();
        
        // Call the callback if provided
        if (callback && typeof callback === 'function') {
          callback();
        }
      })
      .catch(error => {
        console.error('Error updating cart drawer:', error);
        this.fallbackCartUpdate();
      });
  },
  
  /**
   * Sync shipping protection element between old and new cart drawer
   * @param {Element} newCartDrawer - The new cart drawer from the AJAX response
   * @param {Element} currentCartDrawer - The current cart drawer in the DOM
   */
  syncShippingProtectionElement: function(newCartDrawer, currentCartDrawer) {
    // Find shipping protection in both drawers
    const newProtection = newCartDrawer.querySelector('.cart-shipping-protection');
    const currentProtection = currentCartDrawer.querySelector('.cart-shipping-protection');
    
    // If both exist, update the current with new content
    if (newProtection && currentProtection) {
      currentProtection.innerHTML = newProtection.innerHTML;
    } 
    // If only new exists, add it to the current drawer
    else if (newProtection && !currentProtection) {
      const itemsContainer = currentCartDrawer.querySelector('cart-drawer-items');
      if (itemsContainer) {
        itemsContainer.after(newProtection.cloneNode(true));
      }
    }
    // If only current exists but no new one, remove it
    else if (!newProtection && currentProtection) {
      currentProtection.remove();
    }
  },
  
  /**
   * Fallback cart update when the section render API fails
   */
  fallbackCartUpdate: function() {
    // Use the browser's built-in refresh mechanism as a last resort
    window.location.reload();
  },
  
  /**
   * Check if the cart drawer is currently open
   * @return {Boolean} - Whether the cart drawer is open
   */
  isCartDrawerOpen: function() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return false;
    
    return cartDrawer.hasAttribute('open') || cartDrawer.getAttribute('aria-hidden') === 'false';
  },
  
  /**
   * Open the cart drawer
   */
  openCartDrawer: function() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;
    
    // Use the open method if it exists
    if (typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    } else {
      // Fallback for older Dawn versions
      cartDrawer.setAttribute('open', '');
      cartDrawer.setAttribute('aria-hidden', 'false');
    }
  },
  
  /**
   * Update cart count bubbles throughout the site
   * @param {Object} cartData - The cart data from the /cart.js endpoint
   */
  updateCartCountBubbles: function(cartData) {
    const cartCountBubbles = document.querySelectorAll('.cart-count-bubble');
    const itemCount = cartData.item_count;
    
    cartCountBubbles.forEach(bubble => {
      const countSpan = bubble.querySelector('span[data-cart-count]');
      if (countSpan) {
        countSpan.textContent = itemCount;
        countSpan.setAttribute('data-cart-count', itemCount);
      } else {
        bubble.textContent = itemCount;
      }
      
      // Show/hide based on item count
      if (itemCount > 0) {
        bubble.classList.remove('hidden');
      } else {
        bubble.classList.add('hidden');
      }
    });
  },
  
  /**
   * Reinitialize JS components after cart update
   */
  reinitializeCartComponents: function() {
    // Re-bind our event listeners
    this.setupEventListeners();
    
    // Handle any Dawn-specific components
    this.initializeDawnComponents();
  },
  
  /**
   * Initialize Dawn-specific components
   */
  initializeDawnComponents: function() {
    // Quantity inputs
    document.querySelectorAll('quantity-input').forEach(quantityInput => {
      if (typeof quantityInput.init === 'function') {
        try {
          quantityInput.init();
        } catch (e) {
          console.warn('Error initializing quantity input:', e);
        }
      }
    });
    
    // Cart note toggle
    const cartNoteToggle = document.querySelector('.cart-note__toggle');
    if (cartNoteToggle) {
      cartNoteToggle.addEventListener('click', function() {
        const cartNote = document.getElementById('CartNote');
        if (cartNote) {
          cartNote.classList.toggle('hide');
        }
      });
    }
    
    // Try to invoke Dawn's own section load handlers
    try {
      if (window.Shopify && window.Shopify.theme && window.Shopify.theme.sections) {
        const sections = window.Shopify.theme.sections.registered;
        if (sections && sections['cart-drawer']) {
          const instance = sections['cart-drawer'].instances[0];
          if (instance && typeof instance.onSectionLoad === 'function') {
            instance.onSectionLoad();
          }
        }
      }
    } catch (e) {
      console.warn('Error initializing Dawn sections:', e);
    }
  },
  
  /**
   * Set up event listeners for upsell and protection interactions
   */
// In dawn-cart-ajax.js, update the setupEventListeners function:

setupEventListeners: function() {
  // Use event delegation for upsell add buttons
  document.body.addEventListener('click', (e) => {
    const upsellButton = e.target.closest('[data-upsell-add]');
    if (upsellButton) {
      e.preventDefault();
      
      const variantId = upsellButton.getAttribute('data-variant-id');
      if (!variantId) return;
      
      // Disable button and show loading state
      upsellButton.disabled = true;
      upsellButton.textContent = 'Adding...';
      
      this.addItem(variantId, 1, () => {
        // Remove the upsell item after adding
        const upsellContainer = upsellButton.closest('.cart-upsell-item');
        if (upsellContainer) {
          upsellContainer.remove();
        }
      });
    }
  });
  
  // IMPROVED: Use event delegation for protection toggle instead of direct binding
  document.body.addEventListener('change', (e) => {
    const toggle = e.target.closest('.cart-shipping-protection__checkbox');
    if (toggle) {
      const protectionContainer = toggle.closest('.cart-shipping-protection');
      if (protectionContainer) {
        protectionContainer.classList.add('is-loading');
      }
      
      // Store the intended state before the AJAX request
      const isChecked = toggle.checked;
      
      // Get the protection price from the data attribute
      const protectionPrice = toggle.getAttribute('data-protection-price') || '0';
      
      // Dispatch the event with the correct variables
      document.dispatchEvent(new CustomEvent('protection:toggle', {
        bubbles: true,
        detail: {
          enabled: isChecked,
          price: protectionPrice
        }
      }));
      
      if (isChecked) {
        const variantId = toggle.getAttribute('data-protection-variant-id');
        if (variantId) {
          this.addItem(variantId, 1, () => {
            if (protectionContainer) {
              protectionContainer.classList.remove('is-loading');
            }
            // Re-check state after update to ensure it matches user intent
            const updatedToggle = document.querySelector('.cart-shipping-protection__checkbox');
            if (updatedToggle && !updatedToggle.checked) {
              updatedToggle.checked = true;
            }
          });
        }
      } else {
        const itemKey = toggle.getAttribute('data-protection-cart-key');
        if (itemKey) {
          this.changeItem(itemKey, 0, () => {
            if (protectionContainer) {
              protectionContainer.classList.remove('is-loading');
            }
            // Re-check state after update to ensure it matches user intent
            const updatedToggle = document.querySelector('.cart-shipping-protection__checkbox');
            if (updatedToggle && updatedToggle.checked) {
              updatedToggle.checked = false;
            }
          });
        }
      }
    }
  });
},
  
  /**
   * Show loading state on the cart drawer
   */
  showCartLoading: function() {
    // Add loading class to cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.add('cart-drawer--loading');
      
      // Create a loading overlay if it doesn't exist
      let loadingOverlay = cartDrawer.querySelector('.cart-drawer__loading-overlay');
      if (!loadingOverlay) {
        //loadingOverlay = document.createElement('div');
        //loadingOverlay.className = 'cart-drawer__loading-overlay';
        //loadingOverlay.innerHTML = '<div class="loading-overlay__spinner"><svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div>';
        //cartDrawer.appendChild(loadingOverlay);
      } else {
        loadingOverlay.classList.remove('hidden');
      }
    }
  },
  
  /**
   * Hide loading state on the cart drawer
   */
  hideCartLoading: function() {
    // Remove loading class from cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.remove('cart-drawer--loading');
      
      // Hide the loading overlay
      const loadingOverlay = cartDrawer.querySelector('.cart-drawer__loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.classList.add('hidden');
      }
    }
  }
};

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', function() {
  DawnCartAjax.init();
});

// Make available globally
window.DawnCartAjax = DawnCartAjax;